import { useState } from "react";

import { api } from "../lib/api.ts";

/**
 * Per-agent .claude/settings.json overrides — model, hooks, permissions.
 *
 * The CLI materializes whatever object lives here into a temp dir's
 * .claude/settings.json before each `claude -p` invocation, so the host's
 * personal user settings (in ~/.claude/settings.json) merge underneath but
 * the agent-specific bits win.
 *
 * Stored as JSON in agent-claude-settings.json (one entry per agent key).
 * Save = PR against that file, auto-merged on green.
 */
export function AgentClaudeSettingsEditor({
	agentKey,
	initialSettings,
}: {
	agentKey: string;
	initialSettings: Record<string, unknown> | null;
}) {
	const initialText =
		initialSettings && Object.keys(initialSettings).length > 0
			? `${JSON.stringify(initialSettings, null, 2)}\n`
			: "";
	const [text, setText] = useState<string>(initialText);
	const [saved, setSaved] = useState<string>(initialText);
	const [editing, setEditing] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [prUrl, setPrUrl] = useState<string | null>(null);
	const [autoMerged, setAutoMerged] = useState(false);
	const [noopMsg, setNoopMsg] = useState<string | null>(null);

	const dirty = text !== saved;

	function applyPreset(patcher: (obj: Record<string, unknown>) => Record<string, unknown>) {
		setError(null);
		let obj: Record<string, unknown>;
		try {
			obj = text.trim() ? (JSON.parse(text) as Record<string, unknown>) : {};
		} catch (err) {
			setError(`Can't apply preset — JSON is invalid: ${(err as Error).message}`);
			return;
		}
		const next = patcher(obj);
		setText(`${JSON.stringify(next, null, 2)}\n`);
	}

	function presetModel(model: string) {
		applyPreset((obj) => ({ ...obj, model }));
	}

	function presetDenyBash() {
		applyPreset((obj) => {
			const permissions = (obj.permissions as Record<string, unknown> | undefined) ?? {};
			const deny = Array.isArray(permissions.deny) ? [...(permissions.deny as string[])] : [];
			for (const rule of ["Bash(rm -rf:*)", "Bash(sudo:*)", "Bash(curl:*)"]) {
				if (!deny.includes(rule)) deny.push(rule);
			}
			return { ...obj, permissions: { ...permissions, deny } };
		});
	}

	function presetAllowWrites() {
		applyPreset((obj) => {
			const permissions = (obj.permissions as Record<string, unknown> | undefined) ?? {};
			return { ...obj, permissions: { ...permissions, defaultMode: "acceptEdits" } };
		});
	}

	function presetLogHook() {
		applyPreset((obj) => {
			const hooks = (obj.hooks as Record<string, unknown> | undefined) ?? {};
			const existing = Array.isArray(hooks.PreToolUse) ? (hooks.PreToolUse as unknown[]) : [];
			return {
				...obj,
				hooks: {
					...hooks,
					PreToolUse: [
						...existing,
						{
							matcher: "Bash",
							hooks: [
								{
									type: "command",
									command: 'echo "[hook] about to run Bash: $TOOL_INPUT" >&2',
								},
							],
						},
					],
				},
			};
		});
	}

	// Generic "log everything" preset — appends a JSON line per tool call to
	// ~/.claude/g1-agent-hooks.log. Matcher ".*" catches every tool. Claude
	// Code feeds the event payload as JSON on stdin, so we just prepend a
	// timestamp and pipe it in. No external deps (no jq, no python).
	function presetLogAllToolUses() {
		applyPreset((obj) => {
			const hooks = (obj.hooks as Record<string, unknown> | undefined) ?? {};
			const existingPre = Array.isArray(hooks.PreToolUse) ? (hooks.PreToolUse as unknown[]) : [];
			const existingPost = Array.isArray(hooks.PostToolUse) ? (hooks.PostToolUse as unknown[]) : [];
			const logCmd =
				'mkdir -p "$HOME/.claude" && { printf \'[%s] \' "$(date -u +%FT%TZ)"; cat; printf \'\\n\'; } >> "$HOME/.claude/g1-agent-hooks.log"';
			return {
				...obj,
				hooks: {
					...hooks,
					PreToolUse: [
						...existingPre,
						{
							matcher: ".*",
							hooks: [{ type: "command", command: logCmd }],
						},
					],
					PostToolUse: [
						...existingPost,
						{
							matcher: ".*",
							hooks: [{ type: "command", command: logCmd }],
						},
					],
				},
			};
		});
	}

	function presetClear() {
		setText("");
	}

	async function handleSave() {
		setBusy(true);
		setError(null);
		setPrUrl(null);
		setNoopMsg(null);
		try {
			const trimmed = text.trim();
			let payload: Record<string, unknown> | null;
			if (trimmed === "" || trimmed === "{}") {
				payload = null;
			} else {
				try {
					const parsed = JSON.parse(trimmed) as unknown;
					if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
						setError("Settings must be a JSON object (not array / primitive).");
						setBusy(false);
						return;
					}
					payload = parsed as Record<string, unknown>;
					if (Object.keys(payload).length === 0) payload = null;
				} catch (err) {
					setError(`Invalid JSON: ${(err as Error).message}`);
					setBusy(false);
					return;
				}
			}
			const result = await api.setAgentClaudeSettings(agentKey, payload);
			if ("noop" in result) {
				setNoopMsg("No changes — current settings already match.");
				setSaved(text);
				setEditing(false);
				return;
			}
			setPrUrl(result.htmlUrl);
			setAutoMerged(Boolean(result.merged));
			setSaved(text);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	const hasSettings = saved.trim().length > 0 && saved.trim() !== "{}";

	return (
		<section className="mt-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
			<header className="px-4 py-3 border-b border-zinc-800 flex items-baseline justify-between">
				<div>
					<h2 className="text-sm font-semibold text-zinc-100">Claude settings</h2>
					<div className="text-[11px] text-zinc-500 mt-0.5">
						Materialized into <code className="font-mono">.claude/settings.json</code> per task.
						{" "}Merges under the host's user-level settings; agent-specific keys win.
					</div>
				</div>
				{!editing && (
					<button
						type="button"
						onClick={() => {
							setText(saved);
							setEditing(true);
							setError(null);
							setPrUrl(null);
							setNoopMsg(null);
						}}
						className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
					>
						Edit
					</button>
				)}
			</header>

			<div className="p-4 space-y-3">
				{!editing ? (
					!hasSettings ? (
						<div className="text-xs text-zinc-500 italic">
							No per-agent overrides. The host's user settings apply as-is. Click{" "}
							<strong>Edit</strong> to set a model, hooks, or permission rules.
						</div>
					) : (
						<pre className="text-[11px] font-mono text-zinc-300 bg-terminal/60 border border-zinc-800 rounded p-3 overflow-x-auto max-h-72 whitespace-pre">
							{saved}
						</pre>
					)
				) : (
					<>
						<div className="flex flex-wrap gap-1.5">
							<button
								type="button"
								onClick={() => presetModel("claude-opus-4-7")}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono"
								disabled={busy}
							>
								model: opus-4-7
							</button>
							<button
								type="button"
								onClick={() => presetModel("claude-sonnet-4-6")}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono"
								disabled={busy}
							>
								model: sonnet-4-6
							</button>
							<button
								type="button"
								onClick={() => presetModel("claude-haiku-4-5-20251001")}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono"
								disabled={busy}
							>
								model: haiku-4-5
							</button>
							<button
								type="button"
								onClick={presetDenyBash}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300"
								disabled={busy}
							>
								+ deny rm/sudo/curl
							</button>
							<button
								type="button"
								onClick={presetAllowWrites}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300"
								disabled={busy}
							>
								+ defaultMode: acceptEdits
							</button>
							<button
								type="button"
								onClick={presetLogHook}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300"
								disabled={busy}
							>
								+ Bash log hook
							</button>
							<button
								type="button"
								onClick={presetLogAllToolUses}
								className="text-[11px] px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300"
								disabled={busy}
								title="Append JSON event per tool call to ~/.claude/g1-agent-hooks.log"
							>
								+ Log all tool uses
							</button>
							<button
								type="button"
								onClick={presetClear}
								className="text-[11px] px-2 py-1 rounded border border-red-500/30 hover:border-red-500/60 text-red-300/90"
								disabled={busy}
							>
								Clear all
							</button>
						</div>
						<textarea
							value={text}
							onChange={(e) => setText(e.target.value)}
							spellCheck={false}
							placeholder={'{\n  "model": "claude-opus-4-7"\n}'}
							className="w-full h-64 text-[11px] font-mono text-zinc-200 bg-terminal border border-zinc-800 rounded p-3 focus:outline-none focus:border-zinc-600"
							disabled={busy}
						/>
						<div className="text-[11px] text-zinc-500">
							Supported keys: <code className="font-mono">model</code>,{" "}
							<code className="font-mono">hooks</code>,{" "}
							<code className="font-mono">permissions</code>. Other top-level keys are rejected by
							the server.
						</div>
						{error && <div className="text-xs text-red-300">{error}</div>}
						{noopMsg && <div className="text-xs text-zinc-400">{noopMsg}</div>}
						{prUrl && (
							<div className="text-xs text-emerald-300">
								{autoMerged
									? "✓ Auto-merged — restart agent to apply (Ctrl-C + re-run the npx command)."
									: "✓ PR opened — auto-merge didn't take, finish it manually."}{" "}
								<a href={prUrl} target="_blank" rel="noreferrer" className="underline break-all">
									{prUrl}
								</a>
							</div>
						)}
						<div className="flex gap-2 pt-1">
							<button
								type="button"
								onClick={handleSave}
								disabled={busy || !dirty}
								className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
							>
								{busy ? "Opening PR…" : "Save changes (PR)"}
							</button>
							<button
								type="button"
								onClick={() => {
									setEditing(false);
									setText(saved);
									setError(null);
								}}
								disabled={busy}
								className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
							>
								Close
							</button>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
